<template>
    <div class="contractor-document-types-page">
        <app-sub-title text="Tipos de documento contratista" />
        <div class="contractor-document-types-page-buttons">
            <button class="btn btn-primary" @click="openForm" v-if="canCreate">
                <i class="fa fa-plus"></i> Crear tipo
            </button>
        </div>
        <app-contractor-document-types-filters
            :onFilter="onFilter"
            :onClearFilters="onClearFilters"
        />
        <app-contractor-document-types-list
            :data="data"
            :on-change-page="onChangePage"
            :on-edit="onEdit"
            :on-delete="onDelete"
            @sort="onSort"
        />

        <app-pop-up
            id="contractor-document-type-form"
            :title="!docType ? 'Crear tipo' : 'Editar tipo'"
            :on-close="() => $bus.$emit('contractor-document-type-form.clear')"
        >
            <div slot="body">
                <app-contractor-document-type-form
                    :selected-type="docType"
                    :on-save="onSave"
                    id="contractor-document-type-form"
                />
            </div>
            <div slot="buttons">
                <button class="btn btn-primary" :disabled="inProgress" @click="saveType">
                    <i class="fa fa-save"></i> Guardar
                </button>
            </div>
        </app-pop-up>

        <app-pop-up
            id="contractor-document-type-delete-confirm"
            title="Confirmar eliminación"
            :on-close="closeDeleteModal"
        >
            <div slot="body">
                <p class="text-danger">
                    <i class="fa fa-exclamation-triangle"></i>
                    Esta acción no se puede deshacer.
                </p>
                <p>
                    ¿Está seguro de eliminar el tipo
                    <strong>"{{ docTypeToDelete ? docTypeToDelete.name : '' }}"</strong>?
                </p>
            </div>
            <div slot="buttons">
                <button class="btn btn-danger" @click="confirmDelete" :disabled="deleting">
                    <i class="fa fa-trash"></i> Eliminar
                </button>
            </div>
        </app-pop-up>
    </div>
</template>
<script>
import contractorDocumentTypesRepository from '../../../repositories/contractor-document-types.repository';
import SubTitle from '../../UI/Layout/SubTitle.vue';
import ContractorDocumentTypesList from './ContractorDocumentTypesList.vue';
import ContractorDocumentTypesFilters from './ContractorDocumentTypesFilters.vue';
import ContractorDocumentTypeForm from './ContractorDocumentTypeForm.vue';
import { mapGetters } from 'vuex';

export default {
    components: {
        appSubTitle: SubTitle,
        appContractorDocumentTypesList: ContractorDocumentTypesList,
        appContractorDocumentTypesFilters: ContractorDocumentTypesFilters,
        appContractorDocumentTypeForm: ContractorDocumentTypeForm
    },
    data() {
        return {
            data: {},
            page: 1,
            filters: {},
            docType: null,
            docTypeToDelete: null,
            deleting: false,
            sort: { sortBy: 'name', sortDir: 'asc' }
        };
    },
    computed: {
        ...mapGetters(['client', 'user', 'userCapabilities']),
        clientHasContractorModule() {
            const modules = this.client && (this.client.optional_modules || this.client.optionalModules);
            if (!modules || !Array.isArray(modules)) return false;
            return modules.some(m => m.slug === 'contractor');
        },
        canCreate() {
            if (!this.user || !this.user.profile) return false;
            if (['super_admin', 'administrador'].includes(this.user.profile.slug)) return true;
            return this.userCapabilities.includes('create-contractor-document-types');
        }
    },
    methods: {
        async loadTypes() {
            try {
                const params = { ...this.filters, page: this.page };
                if (this.client && this.user && this.user.profile.slug !== 'cliente-admin') {
                    params.clientId = this.client.id;
                }
                const { data } = await contractorDocumentTypesRepository.get(params);
                this.data = Array.isArray(data)
                    ? {
                        data: data,
                        current_page: 1,
                        last_page: 1,
                        per_page: data.length,
                        total: data.length,
                        from: data.length ? 1 : null,
                        to: data.length || null
                    }
                    : data;
            } catch (error) {
                toastr.error('Ocurrió un error al cargar los tipos');
            }
        },
        onChangePage(page) {
            this.page = page;
            this.loadTypes();
        },
        onEdit(item) {
            this.docType = item;
            this.openForm();
        },
        onDelete(item) {
            this.docTypeToDelete = item;
            this.$bus.$emit('contractor-document-type-delete-confirm.open');
        },
        closeDeleteModal() {
            this.$bus.$emit('contractor-document-type-delete-confirm.close');
            this.docTypeToDelete = null;
            this.deleting = false;
        },
        async confirmDelete() {
            if (!this.docTypeToDelete) return;
            this.deleting = true;
            try {
                const { data } = await contractorDocumentTypesRepository.delete(this.docTypeToDelete.id);
                if (data.status) {
                    this.closeDeleteModal();
                    this.loadTypes();
                    toastr.success(data.msg || 'El tipo ha sido eliminado');
                } else {
                    toastr.error(data.msg || 'Error al eliminar el tipo');
                }
            } catch (error) {
                toastr.error('Ocurrió un error al eliminar el tipo');
            } finally {
                this.deleting = false;
            }
        },
        onFilter(filters) {
            this.page = 1;
            this.filters = filters;
            this.loadTypes();
        },
        onClearFilters() {
            this.page = 1;
            this.filters = {};
            this.loadTypes();
        },
        saveType() {
            this.$bus.$emit('contractor-document-type-form.save');
        },
        onSave() {
            this.$bus.$emit('contractor-document-type-form.close');
            this.docType = null;
            this.loadTypes();
        },
        openForm() {
            if (!this.docType) {
                this.$bus.$emit('contractor-document-type-form.clear');
            }
            this.$bus.$emit('contractor-document-type-form.open');
        },
        onSort(ctx) {
            this.sort.sortBy = ctx.sortBy;
            this.sort.sortDir = ctx.sortDir;
            this.loadTypes();
        }
    },
    created() {
        const isClientAdmin = this.user && this.user.profile.slug === 'cliente-admin';
        const canView = this.user && (
            ['super_admin', 'administrador'].includes(this.user.profile.slug) ||
            (this.userCapabilities && this.userCapabilities.includes('view-contractor-document-types'))
        );
        if (!isClientAdmin && !canView) {
            this.$router.push({ name: 'admin' });
            return;
        }
        if (isClientAdmin && !this.clientHasContractorModule) {
            this.$router.push({ name: 'adminClients' });
            return;
        }
        this.loadTypes();
    }
};
</script>
